import { EMPTY_FIELD } from 'constants/emptyField';

import { useState } from 'react';

import EditIcon from '@mui/icons-material/Edit';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import { Box } from '@mui/material';
import { blue } from '@mui/material/colors';
import { useRecordModal } from 'hooks/useRecordModal';
import { ORDER_AMOUNT_ADJUSTMENT_CONFIG } from 'pages/OrderAdjustments/constants/adjustmentConfigs';
import { useIsOrderAdjustmentAllowed } from 'pages/OrderAdjustments/hooks/useIsOrderAdjustmentAllowed';
import { useOrderAdjustmentsForm } from 'pages/OrderAdjustments/hooks/useOrderAdjustmentsForm';
import { OrderAdjustmentsAmountDialog } from 'pages/OrderAdjustments/OrderAdjustmentsAmountDialog/OrderAdjustmentsAmountDialog';
import { OrderAdjustmentsAmountField } from 'pages/OrderAdjustments/OrderAdjustmentsAmountField/OrderAdjustmentsAmountField';
import { OrderAdjustmentsCommentButton } from 'pages/OrderAdjustments/OrderAdjustmentsCommentButton/OrderAdjustmentsCommentButton';
import { OrderAdjustmentsCommentDialog } from 'pages/OrderAdjustments/OrderAdjustmentsCommentDialog/OrderAdjustmentsCommentDialog';
import { OrderAdjustmentsDatagridBulkActionsToolbar } from 'pages/OrderAdjustments/OrderAdjustmentsDatagridBulkActionsToolbar';
import { OrderAdjustmentsDialog } from 'pages/OrderAdjustments/OrderAdjustmentsDialog/OrderAdjustmentsDialog';
import { OrderAdjustmentsExcludeDialog } from 'pages/OrderAdjustments/OrderAdjustmentsExcludeDialog/OrderAdjustmentsExcludeDialog';
import { OrderAdjustmentsGroupCommentDialog } from 'pages/OrderAdjustments/OrderAdjustmentsGroupCommentDialog/OrderAdjustmentsGroupCommentDialog';
import { OrderAdjustmentsProviderField } from 'pages/OrderAdjustments/OrderAdjustmentsProviderField/OrderAdjustmentsProviderField';
import { OrderAdjustmentsResetDialog } from 'pages/OrderAdjustments/OrderAdjustmentsResetDialog/OrderAdjustmentsResetDialog';
import { OrderAdjustmentsSubmitDialog } from 'pages/OrderAdjustments/OrderAdjustmentsSubmitDialog/OrderAdjustmentsSubmitDialog';
import { OrderAdjustmentsSuccessDialog } from 'pages/OrderAdjustments/OrderAdjustmentsSussessDialog/OrderAdjustmentsSuccessDialog';
import { OrderAdjustmentsOrder } from 'pages/OrderAdjustments/types';
import { useTypedTranslate } from 'providers/i18n/hooks/useTypedTranslate';
import { useListContext } from 'react-admin';
import { FormProvider } from 'react-hook-form';
import { Chip } from 'shared/mui/Chips';
import { ContextMenu, ContextMenuButton } from 'shared/mui/ContextMenu';
import { FilterContextProvider } from 'shared/mui/Filter/context/filterContext';
import { FilterClearButton, FilterIdButton } from 'shared/mui/Filter/FilterButtons';
import { FilterReferenceButton } from 'shared/mui/Filter/FilterButtons/FilterReferenceButton/FilterReferenceButton';
import { FilterChipsToolbar } from 'shared/mui/Filter/FilterChipsToolbar/FilterChipsToolbar';
import { FilterIdChips } from 'shared/mui/Filter/FilterId/FilterIdChips/FilterIdChips';
import { FilterIdDialog } from 'shared/mui/Filter/FilterId/FilterIdDialog/FilterIdDialog';
import { FilterIdDialogNotFoundIdAlert } from 'shared/mui/Filter/FilterId/FilterIdDialog/FilterIdDialogNotFoundIdAlert/FilterIdDialogNotFoundIdAlert';
import { FilterToolbar } from 'shared/mui/Filter/FilterToolbar/FilterToolbar';
import { FilterIdTypes, useIdFilters } from 'shared/mui/Filter/hooks/useIdFilters';
import { useSyncFilter } from 'shared/mui/Filter/hooks/useSyncFilter';
import { DatagridFieldLabel } from 'shared/mui/Labels/DatagridFieldLabel/DatagridFieldLabel';
import {
  ChipField,
  DateTimeField,
  EmbeddedFunctionField,
  FunctionField,
  OrderStatusChipField,
  TextField,
} from 'shared/react-admin/Fields';
import { CopyToClipboardField } from 'shared/react-admin/Fields/CopyToClipboardField/CopyToClipboardField';
import { ReferenceField, ReferenceOneField } from 'shared/react-admin/ReferenceFields';
import { MethodsShowDialog } from 'shared/widgets/MethodsShowDialog/MethodsShowDialog';
import { OrderAdjustmentsReferenceDialog } from 'shared/widgets/OrderAdjustmentsReferenceDialog/OrderAdjustmentsReferenceDialog';
import { Method } from 'types/embedded/method';
import { getId } from 'utils';

import { StyledDatagrid } from './OrderAdjustmentsForm.styled';
import { OrderAdjustmentsFormAlertPreview } from './OrderAdjustmentsFormAlerts/OrderAdjustmentsFormAlertPreview';
import { OrderAdjustmentsFormAlertSuccess } from './OrderAdjustmentsFormAlerts/OrderAdjustmentsFormAlertSuccess';

// @TODO - выпилить при добавлении в фильтр FilterIdTypes.Any
type FilterIdTypesWithAnyIdExcluded = Exclude<FilterIdTypes, FilterIdTypes.Any>;

export const OrderAdjustmentsForm = (): JSX.Element => {
  const translate = useTypedTranslate();

  const [currentIdFilter, setCurrentIdFilter] = useState<FilterIdTypesWithAnyIdExcluded>(
    FilterIdTypes.Id,
  );
  const [isOpenIdDialog, setIsOpenIdDialog] = useState<boolean>(false);
  const [isFormSubmitted, setIsFormSubmitted] = useState<boolean>(false);
  const {
    filterValues,
    data: orders = [],
    isFetching,
    onUnselectItems,
  } = useListContext<OrderAdjustmentsOrder>();
  const [requestId, setRequestId] = useState<string>();

  const [
    isOpenOrderAdjustmentsReportReferenceDialog,
    setIsOpenOrderAdjustmentsReportReferenceDialog,
  ] = useState<boolean>(false);

  const {
    record: recordCommentDialog,
    handleCloseRecordModal: handleCloseCommentDialog,
    openRecordModal: openCommentDialog,
    handleOpenRecordModal: handleOpenCommentDialog,
  } = useRecordModal<OrderAdjustmentsOrder>();
  const {
    handleCloseRecordModal: handleCloseGroupCommentDialog,
    openRecordModal: openGroupCommentDialog,
    handleOpenRecordModal: handleOpenGroupCommentDialog,
  } = useRecordModal<OrderAdjustmentsOrder>();
  const {
    record: amountDialogRecord,
    handleCloseRecordModal: handleCloseAmountDialog,
    openRecordModal: openAmountDialog,
    handleOpenRecordModal: handleOpenAmountDialog,
  } = useRecordModal<OrderAdjustmentsOrder>();
  const {
    handleCloseRecordModal: handleCloseAdjustmentsDialog,
    openRecordModal: openAdjustmentsDialog,
    handleOpenRecordModal: handleOpenAdjustmentsDialog,
  } = useRecordModal();
  const {
    handleCloseRecordModal: handleCloseAdjustmentsSubmitDialog,
    openRecordModal: openAdjustmentsSubmitDialog,
    handleOpenRecordModal: handleOpenAdjustmentsSubmitDialog,
  } = useRecordModal();
  const {
    handleCloseRecordModal: handleCloseAdjustmentsSuccessDialog,
    openRecordModal: openAdjustmentsSuccessDialog,
    handleOpenRecordModal: handleOpenAdjustmentsSuccessDialog,
  } = useRecordModal();
  const {
    handleCloseRecordModal: handleCloseAdjustmentsResetDialog,
    openRecordModal: openAdjustmentsResetDialog,
    handleOpenRecordModal: handleOpenAdjustmentsResetDialog,
  } = useRecordModal();
  const {
    record: excludeDialogRecord,
    handleCloseRecordModal: handleCloseAdjustmentsExcludeDialog,
    openRecordModal: openAdjustmentsExcludeDialog,
    handleOpenRecordModal: handleOpenAdjustmentsExcludeDialog,
  } = useRecordModal<OrderAdjustmentsOrder>();
  const {
    record: methodRecordDialog,
    handleCloseRecordModal: handleCloseMethodRecordDialog,
    openRecordModal: openMethodRecordDialog,
    handleOpenRecordModal: handleOpenMethodRecordDialog,
  } = useRecordModal<Method>();

  const {
    setAppliedFilter: setAppliedIdFilter,
    onSubmit: onSubmitIds,
    onReset: onResetIds,
    appliedIdFilters,
    form: formId,
  } = useIdFilters({
    defaultValues: {
      merchantReference: filterValues?.merchantReference || [],
      id: filterValues?.id || [],
      providerReferenceId: filterValues?.providerReferenceId || [],
    },
    resetValues: {
      merchantReference: [],
      id: [],
      providerReferenceId: [],
    },
  });

  useSyncFilter({
    appliedIdFilters,
  });

  const { orderAdjustmentsForm, resetOrderAdjustmentsForm } = useOrderAdjustmentsForm();
  const isOrderAdjustmentAllowed = useIsOrderAdjustmentAllowed();

  const handleOrdersExclude = (records: OrderAdjustmentsOrder[]) => {
    onUnselectItems();

    const updatedIdFilters = appliedIdFilters[currentIdFilter].filter(
      (id: string) => !records.some((record) => id === getId(record[currentIdFilter])),
    );
    if (!updatedIdFilters?.length) {
      onResetIds(currentIdFilter);
      resetOrderAdjustmentsForm();
      return;
    }
    formId.setValue(currentIdFilter, updatedIdFilters);
    onSubmitIds({ ...appliedIdFilters, [currentIdFilter]: updatedIdFilters });
  };

  const isListFilterExist = Object.keys(filterValues).length > 0;
  const notFoundIds = appliedIdFilters.id?.filter(
    (id) => id && !orders?.some((order) => getId(order.id) === id),
  );

  return (
    <>
      <FilterToolbar
        leftActionsSlot={[
          <FilterIdButton
            key="filter-by-id"
            onClick={() => {
              setIsOpenIdDialog(true);
            }}
          />,
          <FilterReferenceButton
            key="info"
            onClick={() => setIsOpenOrderAdjustmentsReportReferenceDialog(true)}
          />,
        ]}
        sx={{ padding: 0, paddingTop: '12px' }}
      />
      <FilterChipsToolbar appliedIdFilters={appliedIdFilters} sx={{ padding: '16px 0' }}>
        <FilterContextProvider
          appliedFilters={appliedIdFilters}
          setAppliedFilter={setAppliedIdFilter}
        >
          <FormProvider {...formId}>
            <FilterIdChips
              omit={[FilterIdTypes.Any]}
              onClick={(type) => {
                setIsOpenIdDialog(true);
                setCurrentIdFilter(type as FilterIdTypesWithAnyIdExcluded);
              }}
            />
            <FilterClearButton
              key={`clear-${currentIdFilter}`}
              label={translate('app.orderAdjustments.filters.clear')}
              onClick={handleOpenAdjustmentsResetDialog}
              visible={isListFilterExist}
            />
          </FormProvider>
        </FilterContextProvider>
      </FilterChipsToolbar>
      <FormProvider {...formId}>
        <FilterIdDialog
          currentIdFilter={currentIdFilter}
          notFoundIds={notFoundIds}
          omit={[FilterIdTypes.Any]}
          onChange={(currentFilter) =>
            setCurrentIdFilter(currentFilter as FilterIdTypesWithAnyIdExcluded)
          }
          onClose={() => setIsOpenIdDialog(false)}
          onReset={onResetIds}
          onSubmit={onSubmitIds}
          open={isOpenIdDialog}
        />
      </FormProvider>
      <FormProvider {...orderAdjustmentsForm}>
        <Box
          sx={{
            minHeight: '100%',
            display: 'initial',
          }}
        >
          {isFormSubmitted && (
            <OrderAdjustmentsFormAlertSuccess
              requestId={requestId}
              setIsFormSubmitted={setIsFormSubmitted}
            />
          )}
          {notFoundIds?.length <= 0 && !isFetching && !isListFilterExist && (
            <OrderAdjustmentsFormAlertPreview
              onClick={() => {
                setIsOpenIdDialog(true);
              }}
            />
          )}
          <FilterIdDialogNotFoundIdAlert ids={notFoundIds} isFetching={!!isFetching} />
          {!isFetching && isListFilterExist && (
            <StyledDatagrid
              bulkActionButtons={
                <OrderAdjustmentsDatagridBulkActionsToolbar
                  handleOpenAdjustmentsDialog={handleOpenAdjustmentsDialog}
                  handleOpenGroupCommentDialog={handleOpenGroupCommentDialog}
                  handleOrdersExclude={handleOrdersExclude}
                />
              }
            >
              <CopyToClipboardField
                label={translate('app.orderAdjustments.datagrid.merchantId')}
                source="merchantReference"
                underline
              />
              <DateTimeField
                label={translate('app.orderAdjustments.datagrid.created')}
                source="createdAt"
              />
              <OrderAdjustmentsAmountField
                label={
                  <DatagridFieldLabel
                    primaryText={translate('app.orderAdjustments.datagrid.amount')}
                    secondaryText={translate('app.orderAdjustments.datagrid.orderOrTx')}
                  />
                }
                source="amount"
              />
              <ReferenceField
                label={translate('app.orderAdjustments.datagrid.direction')}
                reference="directions"
                source="direction"
              >
                <ChipField source="description" />
              </ReferenceField>
              <ReferenceField
                label={translate('app.orderAdjustments.datagrid.status')}
                reference="new/order_statuses"
                sortable={false}
                source="status"
              >
                <OrderStatusChipField source="description" />
              </ReferenceField>
              <ReferenceOneField
                label={translate('app.orderAdjustments.datagrid.client')}
                link={false}
                reference="customers"
                sortable={false}
                source="customer"
                target="id"
              >
                <TextField source="customerId" />
              </ReferenceOneField>
              <EmbeddedFunctionField
                cellClassName="column-merchant"
                label={translate('app.orderAdjustments.datagrid.merchant')}
                render={(value) => <Chip label={value} />}
                sourceField="merchant.name"
              />
              <OrderAdjustmentsProviderField
                label={
                  <DatagridFieldLabel
                    primaryText={translate('app.orderAdjustments.datagrid.provider')}
                    secondaryText={translate('app.orderAdjustments.datagrid.paymentSystem')}
                  />
                }
                source="provider"
              />
              <EmbeddedFunctionField
                cellClassName="column-gateway"
                label={translate('app.orderAdjustments.datagrid.gateway')}
                sortable={false}
                sourceField="gateway.name"
              />
              <ReferenceOneField
                emptyText={EMPTY_FIELD}
                label={translate('app.orderAdjustments.datagrid.method')}
                link={false}
                reference="methods"
                sortable={false}
                source="method.@id"
                target="id"
              >
                <FunctionField
                  render={(method) => (
                    <TextField
                      onClick={() => handleOpenMethodRecordDialog(method)}
                      source="name"
                      sx={{
                        cursor: 'pointer',
                        color: blue[700],
                        textDecoration: 'underline',
                      }}
                    />
                  )}
                />
              </ReferenceOneField>
              <ContextMenu
                render={(record: OrderAdjustmentsOrder) => [
                  isOrderAdjustmentAllowed(ORDER_AMOUNT_ADJUSTMENT_CONFIG, [record]) && (
                    <ContextMenuButton
                      icon={<EditIcon color="secondary" />}
                      key="edit-amount-button"
                      label={translate('app.orderAdjustments.contextMenu.editAmount')}
                      onClick={() => handleOpenAmountDialog(record)}
                    />
                  ),
                  <OrderAdjustmentsCommentButton
                    key="comment-button"
                    onClick={() => handleOpenCommentDialog(record)}
                    orderId={record.id}
                  />,
                  <ContextMenuButton
                    icon={<RemoveCircleOutlineIcon color="secondary" />}
                    key="exclude-button"
                    label={translate('app.orderAdjustments.contextMenu.exclude')}
                    onClick={() => handleOpenAdjustmentsExcludeDialog(record)}
                  />,
                ]}
              />
            </StyledDatagrid>
          )}
        </Box>
        {recordCommentDialog && (
          <OrderAdjustmentsCommentDialog
            onClose={handleCloseCommentDialog}
            open={openCommentDialog}
            record={recordCommentDialog}
          />
        )}
        {openGroupCommentDialog && (
          <OrderAdjustmentsGroupCommentDialog
            onClose={handleCloseGroupCommentDialog}
            open={openGroupCommentDialog}
          />
        )}
        {amountDialogRecord && (
          <OrderAdjustmentsAmountDialog
            onClose={handleCloseAmountDialog}
            open={openAmountDialog}
            record={amountDialogRecord}
          />
        )}
        {openAdjustmentsDialog && (
          <OrderAdjustmentsDialog
            onClose={handleCloseAdjustmentsDialog}
            onSubmit={handleOpenAdjustmentsSubmitDialog}
            open={openAdjustmentsDialog}
          />
        )}
        {openAdjustmentsSubmitDialog && (
          <OrderAdjustmentsSubmitDialog
            onClose={handleCloseAdjustmentsSubmitDialog}
            onSuccess={({ requestId }) => {
              setIsFormSubmitted(true);
              onResetIds(currentIdFilter);
              resetOrderAdjustmentsForm();
              setRequestId(requestId);
              handleOpenAdjustmentsSuccessDialog();
            }}
            open={openAdjustmentsSubmitDialog}
          />
        )}
        {requestId && (
          <OrderAdjustmentsSuccessDialog
            onClose={handleCloseAdjustmentsSuccessDialog}
            open={openAdjustmentsSuccessDialog}
            requestId={requestId}
          />
        )}
        {openAdjustmentsResetDialog && (
          <OrderAdjustmentsResetDialog
            onClose={handleCloseAdjustmentsResetDialog}
            onReset={() => {
              onResetIds(currentIdFilter);
              resetOrderAdjustmentsForm();
              handleCloseAdjustmentsResetDialog();
            }}
            open={openAdjustmentsResetDialog}
          />
        )}
        {isOpenOrderAdjustmentsReportReferenceDialog && (
          <OrderAdjustmentsReferenceDialog
            onClose={() => setIsOpenOrderAdjustmentsReportReferenceDialog(false)}
            open={isOpenOrderAdjustmentsReportReferenceDialog}
          />
        )}
        {excludeDialogRecord && (
          <OrderAdjustmentsExcludeDialog
            onClose={handleCloseAdjustmentsExcludeDialog}
            onExclude={() => {
              handleOrdersExclude([excludeDialogRecord]);
              handleCloseAdjustmentsExcludeDialog();
            }}
            open={openAdjustmentsExcludeDialog}
            selectedOrder={excludeDialogRecord}
          />
        )}
        {methodRecordDialog && (
          <MethodsShowDialog
            method={methodRecordDialog}
            onClose={handleCloseMethodRecordDialog}
            open={openMethodRecordDialog}
          />
        )}
      </FormProvider>
    </>
  );
};
